import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Session } from '@om/shared-auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MessageList, type Message, type RichMessageData, type MessageOption } from '@/components/celebration-chat/MessageList';
import { InputControls } from '@/components/celebration-chat/InputControls';
import {
  sendChatCreationMessage,
  getChatSession,
  trackChatAnalyticsEvent,
  normalizeCelebrationDraft,
  type ChatCreationResponse,
  type ChatTemplateContext,
  type ChatSuggestedEvent,
  type ChatSuggestedActivity,
  type ChatSuggestedGoal,
  type ChatSuggestedService,
  type ChatSessionData,
} from '@/lib/api/chatCreation.service';
import type { CelebrationDraftDto } from '@om/shared-types';
import * as celebrantConfig from '@om/shared-types/celebrant-config';
const { CELEBRANT_ROLES_BY_TYPE } = celebrantConfig;
import { buildAuthRedirectToUrl } from '@om/shared-auth';
import { CreationPreviewPanel } from '@/components/celebration-chat/CreationPreviewPanel';
import { CalendarPickerModal } from '@/components/celebration-chat/CalendarPickerModal';
import { SplitCreationLayout } from '@/components/layout/SplitCreationLayout';
import { createCelebrationWithWorkflow } from '@/lib/celebrations/createCelebrationWorkflow';
import { buildCreateWorkflowInput } from '@/lib/celebrations/workflowInput';
import { clearCrossModeDraft, readCrossModeDraft, writeCrossModeDraft } from '@/lib/celebrations/crossModeDraft';
import { handleApiError } from '@/lib/api/client';
import { celebrationService } from '@/lib/api/celebration.service';
import { useAuth } from '@/hooks/useAuth';
import { logError } from '@/lib/utils';
import { CELEBRATION_TYPES, getSubtypesForType } from '@/config/celebrationRegistry';

const CREATE_CELEBRATION_OPTION_VALUE = '__create_celebration__';
const LOGIN_SIGNUP_OPTION_VALUE = '__login_signup__';

/**
 * Build quick-reply chip options for any field that has a known finite set of choices.
 * Returns undefined when no chips apply (freeform field or no options for the type).
 */
function buildQuickReplies(
  missingFields: string[] | undefined,
  extractedFields: CelebrationDraftDto | undefined,
): MessageOption[] | undefined {
  if (!missingFields || missingFields.length === 0) return undefined;

  const celebrationType = (extractedFields?.celebration_type as string | undefined)?.toLowerCase() ?? '';

  // celebration_type chips — show all public types with emojis
  if (missingFields.includes('celebration_type')) {
    return CELEBRATION_TYPES
      .filter((t) => !t.internal)
      .map((t) => ({
        id: `type-${t.key}`,
        label: `${t.emoji}\u00a0${t.label}`,
        value: t.key,
        variant: 'chip' as const,
      }));
  }

  // celebration_subtype chips — subtypes for the current type (only when type has subtypes)
  if (missingFields.includes('celebration_subtype') && celebrationType) {
    const subtypes = getSubtypesForType(celebrationType);
    if (subtypes.length > 0) {
      return subtypes.map((s) => ({
        id: `subtype-${s.key}`,
        label: s.label,
        value: s.key,
        variant: 'chip' as const,
      }));
    }
  }

  // _celebrant_role chips — role options contextual to celebration type
  if (missingFields.includes('_celebrant_role') && celebrationType) {
    const roles = CELEBRANT_ROLES_BY_TYPE[celebrationType] ?? CELEBRANT_ROLES_BY_TYPE['other'];
    return roles.map((r) => ({
      id: `role-${r.toLowerCase().replace(/\s+/g, '-')}`,
      label: r,
      value: r,
      variant: 'chip' as const,
    }));
  }

  // start_date, end_date, rsvp_deadline chips — calendar picker + shortcuts + venue TBD
  if (
    missingFields.includes('start_date') ||
    missingFields.includes('end_date') ||
    missingFields.includes('rsvp_deadline')
  ) {
    const options: MessageOption[] = [
      {
        id: 'quick-calendar-picker',
        label: '📅 Pick Date on Calendar',
        value: '__open_calendar__',
        variant: 'chip' as const,
      },
    ];

    if (missingFields.includes('location_name')) {
      options.push({
        id: 'quick-venue-tbd',
        label: 'Venue is TBD',
        value: 'Venue is TBD',
        variant: 'chip' as const,
      });
    }

    options.push(
      { id: 'quick-date-this-weekend', label: 'This Weekend', value: 'This Weekend', variant: 'chip' as const },
      { id: 'quick-date-next-month', label: 'Next Month', value: 'Next Month', variant: 'chip' as const },
      { id: 'quick-date-in-3-months', label: 'In 3 Months', value: 'In 3 Months', variant: 'chip' as const },
      { id: 'quick-date-in-6-months', label: 'In 6 Months', value: 'In 6 Months', variant: 'chip' as const },
      { id: 'quick-date-next-year', label: 'Next Year', value: 'Next Year', variant: 'chip' as const },
    );

    return options;
  }

  // location_name alone (e.g. if venue is asked without date)
  if (missingFields.includes('location_name')) {
    return [
      {
        id: 'quick-venue-tbd',
        label: 'Venue is TBD',
        value: 'Venue is TBD',
        variant: 'chip' as const,
      },
    ];
  }

  return undefined;
}


const toStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }
  return [];
};

type ChatCreateMappingError = {
  stage: string;
  message: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const isSuppressedCreateWarningMessage = (message: string): boolean => {
  const normalized = message.trim().toLowerCase();
  return normalized.startsWith('celebration remains draft until');
};

const getWorkflowWarnings = (value: unknown): string[] => {
  if (!isRecord(value) || !Array.isArray(value.warnings)) {
    return [];
  }

  return value.warnings
    .map((warning) => String(warning).trim())
    .filter((warning) => Boolean(warning) && !isSuppressedCreateWarningMessage(warning));
};

const getWorkflowMappingErrors = (value: unknown): ChatCreateMappingError[] => {
  if (!isRecord(value) || !Array.isArray(value.mappingErrors)) {
    return [];
  }

  return value.mappingErrors
    .filter(isRecord)
    .map((mappingError) => {
      const stage = typeof mappingError.stage === 'string' && mappingError.stage.trim().length > 0
        ? mappingError.stage.trim()
        : 'unknown_stage';

      const message = typeof mappingError.message === 'string' && mappingError.message.trim().length > 0
        ? mappingError.message.trim()
        : typeof mappingError.details === 'string' && mappingError.details.trim().length > 0
          ? mappingError.details.trim()
          : 'An item could not be mapped automatically.';

      return { stage, message };
    })
    .filter((mappingError) => !isSuppressedCreateWarningMessage(mappingError.message));
};

const formatWarningsMessage = (warnings: string[]): string => {
  return `Warnings:\n${warnings.map((warning) => `- ${warning}`).join('\n')}`;
};

const formatMappingErrorsMessage = (mappingErrors: ChatCreateMappingError[]): string => {
  return `Mapping issues:\n${mappingErrors.map((mappingError) => `- [${mappingError.stage}] ${mappingError.message}`).join('\n')}`;
};

/**
 * CelebrationChatCreation — chat-driven celebration creation page.
 * Mounted at /celebrations/new (default mode).
 *
 * Flow:
 *   1. Conversationally collects celebration details
 *   2. Shows SummaryCard for user confirmation
 *   3. Suggests matching templates via TemplateCards
 *   4. Creates celebration and shows CreationSuccessCard
 */
export const CelebrationChatCreation: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, isLoading: isAuthLoading, user } = useAuth();
  const { t } = useTranslation(['celebrations']);
  const urlSessionId = searchParams.get('session') ?? undefined;
  const urlDraftId = searchParams.get('draft') ?? undefined;
  const initialPrompt = searchParams.get('prompt')?.trim() ?? '';

  const [messages, setMessages] = useState<Message[]>([]);
  useEffect(() => {
    setMessages([
      {
        id: 'welcome-intro',
        content: t('celebrations:chatCreation.welcome.greeting'),
        isUser: false,
        timestamp: new Date(),
      },
      {
        id: 'welcome-question',
        content: t('celebrations:chatCreation.welcome.question'),
        isUser: false,
        timestamp: new Date(),
      },
    ]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [draftId, setDraftId] = useState<string | undefined>(urlDraftId);
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [isComplete, setIsComplete] = useState(false);
  const [workflowStatus, setWorkflowStatus] = useState<string>('start');
  const [liveExtractedFields, setLiveExtractedFields] = useState<CelebrationDraftDto>({});
  const [sharedDraftHydrationComplete, setSharedDraftHydrationComplete] = useState(!isAuthenticated);
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [generatedTags, setGeneratedTags] = useState<Record<string, string[]>>({});
  const [confirmedTags, setConfirmedTags] = useState<Record<string, string[]>>({});
  const [eventSuggestions, setEventSuggestions] = useState<ChatSuggestedEvent[]>([]);
  const [selectedEvents, setSelectedEvents] = useState<ChatSuggestedEvent[]>([]);
  const [selectedActivities, setSelectedActivities] = useState<Record<string, string[]>>({});
  const [activityReviewCurrentEventIndex, setActivityReviewCurrentEventIndex] = useState(0);
  const [activitySuggestions, setActivitySuggestions] = useState<Record<string, ChatSuggestedActivity[]>>({});
  const [goalSuggestions, setGoalSuggestions] = useState<Record<string, ChatSuggestedGoal[]>>({});
  const [selectedGoalTemplateIdsByEvent, setSelectedGoalTemplateIdsByEvent] = useState<Record<string, number[]>>({});
  const [serviceSuggestions, setServiceSuggestions] = useState<ChatSuggestedService[]>([]);
  const [selectedServiceDefinitionIds, setSelectedServiceDefinitionIds] = useState<number[]>([]);
  const [addServicesToShortlist, setAddServicesToShortlist] = useState<boolean | undefined>(undefined);
  const [isHydrated, setIsHydrated] = useState(false);
  const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false);
  const [awaitingAuthForCreate, setAwaitingAuthForCreate] = useState(false);
  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false);

  const authPopupRef = useRef<Window | null>(null);
  const authCompletionHandledRef = useRef(false);
  const createActionCtaStateRef = useRef<'guest' | 'authenticated' | null>(null);
  const hasConsumedInitialPromptRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setSharedDraftHydrationComplete(true);
      return;
    }

    setSharedDraftHydrationComplete(false);

    let cancelled = false;
    void (async () => {
      try {
        const serverDraft = draftId
          ? await celebrationService.getDraft(draftId)
          : await celebrationService.createDraft({ mode: 'ai', resume_state: {} });
        if (cancelled) return;

        setDraftId(serverDraft.draft_id);
        if (!draftId) {
          setSearchParams((current) => {
            current.set('draft', serverDraft.draft_id);
            return current;
          }, { replace: true });
        }
        if (Object.keys(serverDraft.draft).length > 0) {
          setLiveExtractedFields((current) => normalizeCelebrationDraft({ ...current, ...serverDraft.draft }));
        }
      } catch {
        // AI chat remains usable with its existing Redis/local session fallback.
      } finally {
        if (!cancelled) setSharedDraftHydrationComplete(true);
      }
    })();

    return () => { cancelled = true; };
  }, [draftId, isAuthenticated, setSearchParams]);

  useEffect(() => {
    if (!draftId || !isAuthenticated || !isHydrated || !sharedDraftHydrationComplete || isComplete) return;
    const timer = window.setTimeout(() => {
      void celebrationService.patchDraft(draftId, {
        draft: liveExtractedFields,
        mode: 'ai',
        resume_state: { chat_session_id: sessionId },
      }).catch(() => undefined);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [draftId, isAuthenticated, isComplete, isHydrated, liveExtractedFields, sessionId, sharedDraftHydrationComplete]);

  const isCreateActionCtaMessage = useCallback((message: Message) => {
    const normalizedContent = message.content.trim().toLowerCase();
    const hasCtaOption = message.options?.some(
      (option) => option.value === CREATE_CELEBRATION_OPTION_VALUE || option.value === LOGIN_SIGNUP_OPTION_VALUE,
    ) ?? false;

    const createActionMarkers = [
      'to create celebration please login or signup',
      'to create your celebration, please log in or sign up first',
      'click create celebration when ready',
      'click create celebration now when ready',
      'click create celebration and i will finalize it',
    ];

    return hasCtaOption || createActionMarkers.some((marker) => normalizedContent.includes(marker));
  }, []);

  const isCreateActionValidationPassed = useMemo(() => {
    if (missingFields.length > 0) return false;

    const title = typeof liveExtractedFields.title === 'string' ? liveExtractedFields.title.trim() : '';
    const celebrationTypeRaw = typeof liveExtractedFields.celebration_type === 'string'
      ? liveExtractedFields.celebration_type.trim()
      : '';
    if (!title || !celebrationTypeRaw) return false;

    const celebrationType = celebrationTypeRaw.toLowerCase();
    const celebrantNames = toStringArray(liveExtractedFields.celebrant_names);

    const celebrantsRequiredTypes = ['wedding', 'engagement', 'anniversary'];
    const needsCelebrants = celebrantsRequiredTypes.some((type) => celebrationType.includes(type));
    if (needsCelebrants && celebrantNames.length === 0) return false;

    return true;
  }, [missingFields, liveExtractedFields]);

  const trackedSessionStartRef = useRef<string | null>(null);
  const trackedStatusRef = useRef<string | null>(null);
  // Prevents hydration effect from re-running when we're in the middle of signing
  // out a guest session before redirecting to /auth.
  const isNavigatingToAuthRef = useRef(false);
  // Mutable mirror of interrupt-relevant state so beforeunload/pagehide handlers
  // always read current values without the effect needing state deps.
  const interruptStateRef = useRef({ sessionId: undefined as string | undefined, workflowStatus: 'start', isComplete: false });

  const PENDING_INTERRUPT_KEY = 'celebration_chat_pending_interrupt';
  const DRAFT_KEY = 'celebration_chat_draft_v1';

  const trackEvent = useCallback(async (
    eventType: 'chat_session_started' | 'chat_step_changed' | 'chat_message_sent' | 'chat_selection_made' | 'chat_skip_used' | 'chat_create_clicked' | 'chat_interrupted' | 'chat_abandoned',
    payload?: {
      sessionId?: string;
      workflowStatus?: string;
      step?: string;
      reason?: string;
      metadata?: Record<string, unknown>;
    },
  ) => {
    const effectiveSessionId = payload?.sessionId || sessionId;
    if (!effectiveSessionId) return;

    await trackChatAnalyticsEvent({
      sessionId: effectiveSessionId,
      eventType,
      workflowStatus: payload?.workflowStatus || workflowStatus,
      step: payload?.step,
      reason: payload?.reason,
      metadata: payload?.metadata,
    });
  }, [sessionId, workflowStatus]);

  useEffect(() => {
    const greetingName = user?.full_name || user?.username;
    if (!greetingName) return;
    const firstName = greetingName.split(' ')[0]?.trim();
    if (!firstName) return;

    setMessages((prev) => {
      if (prev.length < 2 || prev[0].id !== 'welcome-intro' || prev[1].id !== 'welcome-question') return prev;
      return [
        {
          ...prev[0],
          content: t('celebrations:chatCreation.welcome.greetingNamed', { firstName }),
        },
        ...prev.slice(1),
      ];
    });
  }, [user?.full_name, user?.username]);


  // Keep completion state aligned with workflow status so stale state does not
  // leave the composer disabled when we are no longer in a terminal create step.
  useEffect(() => {
    if (workflowStatus !== 'action_executed' && isComplete) {
      setIsComplete(false);
    }
  }, [workflowStatus, isComplete]);

  /**
   * Attach richData to ALL relevant messages when restoring a session.
   * - Tag review card: attached to any bot message whose content mentions "tags",
   *   regardless of current status (so it stays visible in history after confirmation).
   * - Event review card: attached to the relevant bot message when session has events.
   * - Activity review card: attached to the relevant bot message when session has activities.
   * Returns the rebuilt array with richData patched in.
   */
  const attachHistoricalRichData = (msgs: Message[], session: ChatSessionData): Message[] => {
    const hasGeneratedTags = session.generatedTags && Object.keys(session.generatedTags).length > 0;
    const hasEvents = session.eventSuggestions && session.eventSuggestions.length > 0;
    const hasActivities =
      session.selectedEvents && session.selectedEvents.length > 0 &&
      session.activitySuggestions && Object.keys(session.activitySuggestions).length > 0;
    const hasGoals =
      session.selectedEvents && session.selectedEvents.length > 0 &&
      session.goalSuggestions && Object.keys(session.goalSuggestions).length > 0;
    const hasServices = session.serviceSuggestions && session.serviceSuggestions.length > 0;

    // Track whether each card type has already been attached so it only appears once
    let tagCardAttached = false;
    let eventCardAttached = false;
    let activityCardAttached = false;
    let goalCardAttached = false;
    let serviceCardAttached = false;

    return msgs.map((msg) => {
      if (msg.isUser) return msg;

      // Tag review — attach to the first matching bot message only
      if (!tagCardAttached && hasGeneratedTags && /picked out.*\*\*\d+.*tag|tags? that fit/i.test(msg.content)) {
        tagCardAttached = true;
        return { ...msg, richData: { type: 'tag_review', tags: session.generatedTags! } };
      }

      // Event review — attach to the first matching bot message only
      if (!eventCardAttached && hasEvents && /events? that fit|pick the ones|recommended events/i.test(msg.content)) {
        eventCardAttached = true;
        return {
          ...msg,
          richData: {
            type: 'event_review',
            events: session.eventSuggestions!,
            activitySuggestions: session.activitySuggestions,
            goalSuggestions: session.goalSuggestions,
          },
        };
      }

      // Activity review — attach to the first matching bot message only
      if (!activityCardAttached && hasActivities && /activit|things to do|tasks? for/i.test(msg.content)) {
        activityCardAttached = true;
        return {
          ...msg,
          richData: {
            type: 'activity_review',
            selectedEvents: session.selectedEvents!,
            activitySuggestions: session.activitySuggestions!,
            activityReviewCurrentEventIndex: session.activityReviewCurrentEventIndex,
            selectedActivities: session.selectedActivities,
          },
        };
      }

      // Goal review — attach to the first matching bot message only
      if (!goalCardAttached && hasGoals && /goal|auto-add.*goal|review.*goal/i.test(msg.content)) {
        goalCardAttached = true;
        return {
          ...msg,
          richData: {
            type: 'goal_review',
            selectedEvents: session.selectedEvents!,
            goalSuggestions: session.goalSuggestions!,
          },
        };
      }

      if (!serviceCardAttached && hasServices && /service|shortlist/i.test(msg.content)) {
        serviceCardAttached = true;
        return {
          ...msg,
          richData: {
            type: 'service_review',
            services: session.serviceSuggestions!,
            selectedServiceDefinitionIds: session.selectedServiceDefinitionIds,
          },
        };
      }

      return msg;
    });
  };

  // Single async hydration effect — URL param first, then localStorage fallback
  useEffect(() => {
    // Suppress re-hydration triggered by the guest sign-out before /auth redirect
    if (isNavigatingToAuthRef.current) return;

    setIsHydrated(false);

    void (async () => {
      try {
        // ── 1. Process any pending interrupt analytics ─────────────────
        const pendingRaw = window.localStorage.getItem(PENDING_INTERRUPT_KEY);
        if (pendingRaw) {
          window.localStorage.removeItem(PENDING_INTERRUPT_KEY);
          try {
            const pending = JSON.parse(pendingRaw) as {
              sessionId?: string;
              workflowStatus?: string;
              reason?: string;
              at?: string;
            };
            if (pending.sessionId) {
              void trackEvent('chat_interrupted', {
                sessionId: pending.sessionId,
                workflowStatus: pending.workflowStatus,
                reason: pending.reason || 'page_refresh_or_close',
                metadata: { interruptedAt: pending.at },
              });
            }
          } catch { /* ignore malformed */ }
        }

        // ── 2. Determine sessionId to restore ──────────────────────────
        //    URL ?session= param is the primary source.
        //    No param → fresh start (clear stale localStorage draft).
        if (!urlSessionId) {
          window.localStorage.removeItem(DRAFT_KEY);
          setMessages([
            {
              id: 'welcome-intro',
              content: t('celebrations:chatCreation.welcome.greeting'),
              isUser: false,
              timestamp: new Date(),
            },
            {
              id: 'welcome-question',
              content: t('celebrations:chatCreation.welcome.question'),
              isUser: false,
              timestamp: new Date(),
            },
          ]);
          setSessionId(undefined);
          setConversationId(undefined);
          setWorkflowStatus('start');
          setLiveExtractedFields({});
          setMissingFields([]);
          setGeneratedTags({});
          setConfirmedTags({});
          setEventSuggestions([]);
          setSelectedEvents([]);
          setActivitySuggestions({});
          setSelectedActivities({});
          setActivityReviewCurrentEventIndex(0);
          setGoalSuggestions({});
          setSelectedGoalTemplateIdsByEvent({});
          setServiceSuggestions([]);
          setSelectedServiceDefinitionIds([]);
          setAddServicesToShortlist(undefined);
          setIsLoading(false);
          setIsComplete(false);
          hasConsumedInitialPromptRef.current = false;
          trackedSessionStartRef.current = null;
          trackedStatusRef.current = null;
          createActionCtaStateRef.current = null;
          return;
        }

        // ── 3. Fetch live session from backend (Redis) ─────────────────
        const session = await getChatSession(urlSessionId);

        if (session) {
          // Rebuild messages from conversation history
          const now = new Date();
          const rebuilt: Message[] = session.conversationHistory.map((entry, idx) => ({
            id: `restored-${idx}`,
            content: entry.content,
            isUser: entry.role === 'user',
            timestamp: now,
          }));

          // Attach rich cards to all relevant messages (tag, event, activity)
          const rebuiltWithRich = attachHistoricalRichData(rebuilt, session);

          setMessages((prev) => (rebuiltWithRich.length > 0 ? rebuiltWithRich : prev));
          setSessionId(session.sessionId);
          setWorkflowStatus(session.status);
          setIsComplete(session.status === 'action_executed');
          setLiveExtractedFields(session.extractedFields ?? {});
          setMissingFields(session.missingFields ?? []);
          if (session.generatedTags) setGeneratedTags(session.generatedTags);
          if (session.confirmedTags && !['awaiting_tag_review', 'awaiting_intake_confirmation'].includes(session.status)) {
            setConfirmedTags(session.confirmedTags);
          } else if (['awaiting_tag_review', 'awaiting_intake_confirmation'].includes(session.status)) {
            setConfirmedTags({});
          }
          if (session.eventSuggestions) setEventSuggestions(session.eventSuggestions);
          if (typeof session.activityReviewCurrentEventIndex === 'number') {
            setActivityReviewCurrentEventIndex(session.activityReviewCurrentEventIndex);
          }
          const preAcceptanceStatuses = ['awaiting_intake_confirmation', 'awaiting_tag_review', 'awaiting_event_review'];
          const preActivityStatuses = preAcceptanceStatuses;
          const preGoalStatuses = [...preActivityStatuses, 'awaiting_activity_review'];
          if (session.selectedEvents && !preAcceptanceStatuses.includes(session.status)) {
            setSelectedEvents(session.selectedEvents);
          } else if (preAcceptanceStatuses.includes(session.status)) {
            setSelectedEvents([]);
          }
          if (session.activitySuggestions) setActivitySuggestions(session.activitySuggestions);
          if (session.selectedActivities && !preActivityStatuses.includes(session.status)) {
            setSelectedActivities(session.selectedActivities);
          } else if (preActivityStatuses.includes(session.status)) {
            setSelectedActivities({});
          }
          if (session.goalSuggestions) setGoalSuggestions(session.goalSuggestions);
          if (session.selectedGoalTemplateIdsByEvent && !preGoalStatuses.includes(session.status)) {
            setSelectedGoalTemplateIdsByEvent(session.selectedGoalTemplateIdsByEvent);
          } else if (preGoalStatuses.includes(session.status)) {
            setSelectedGoalTemplateIdsByEvent({});
          }
          if (session.serviceSuggestions) {
            setServiceSuggestions(session.serviceSuggestions);
          }
          if (Array.isArray(session.selectedServiceDefinitionIds)) {
            setSelectedServiceDefinitionIds(session.selectedServiceDefinitionIds);
          }
          if (typeof session.addServicesToShortlist === 'boolean') {
            setAddServicesToShortlist(session.addServicesToShortlist);
          }

          // Pre-set analytics refs so hydrated sessions don't re-fire start/step events
          trackedSessionStartRef.current = session.sessionId;
          trackedStatusRef.current = session.status;

          // Also read conversationId from localStorage draft if available
          const raw = window.localStorage.getItem(DRAFT_KEY);
          if (raw) {
            try {
              const localDraft = JSON.parse(raw) as { conversationId?: string };
              if (localDraft.conversationId) setConversationId(localDraft.conversationId);
            } catch { /* ignore */ }
          }
          return;
        }

        // ── 4. Backend session gone (expired) — fall back to localStorage ─
        const raw = window.localStorage.getItem(DRAFT_KEY);
        if (raw) {
          try {
            const localDraft = JSON.parse(raw) as {
              messages?: Array<{ id: string; content: string; isUser: boolean; timestamp: string; richData?: RichMessageData }>;
              sessionId?: string;
              conversationId?: string;
              workflowStatus?: string;
              liveExtractedFields?: CelebrationDraftDto;
              missingFields?: string[];
              generatedTags?: Record<string, string[]>;
              confirmedTags?: Record<string, string[]>;
              eventSuggestions?: ChatSuggestedEvent[];
              selectedEvents?: ChatSuggestedEvent[];
              activitySuggestions?: Record<string, ChatSuggestedActivity[]>;
              selectedActivities?: Record<string, string[]>;
              activityReviewCurrentEventIndex?: number;
              goalSuggestions?: Record<string, ChatSuggestedGoal[]>;
              selectedGoalTemplateIdsByEvent?: Record<string, number[]>;
              serviceSuggestions?: ChatSuggestedService[];
              selectedServiceDefinitionIds?: number[];
              addServicesToShortlist?: boolean;
            };
            if (localDraft.sessionId === urlSessionId) {
              if (Array.isArray(localDraft.messages) && localDraft.messages.length > 0) {
                setMessages(localDraft.messages.map((msg) => ({ ...msg, timestamp: new Date(msg.timestamp) })));
              }
              if (localDraft.sessionId) setSessionId(localDraft.sessionId);
              if (localDraft.conversationId) setConversationId(localDraft.conversationId);
              if (localDraft.workflowStatus) setWorkflowStatus(localDraft.workflowStatus);
              setIsComplete(localDraft.workflowStatus === 'action_executed');
              if (localDraft.liveExtractedFields) {
                setLiveExtractedFields(normalizeCelebrationDraft(localDraft.liveExtractedFields));
              }
              if (Array.isArray(localDraft.missingFields)) setMissingFields(localDraft.missingFields);
              if (localDraft.generatedTags) setGeneratedTags(localDraft.generatedTags);
              if (localDraft.confirmedTags && !['awaiting_tag_review', 'awaiting_intake_confirmation'].includes(localDraft.workflowStatus ?? '')) {
                setConfirmedTags(localDraft.confirmedTags);
              } else if (['awaiting_tag_review', 'awaiting_intake_confirmation'].includes(localDraft.workflowStatus ?? '')) {
                setConfirmedTags({});
              }
              if (Array.isArray(localDraft.eventSuggestions)) setEventSuggestions(localDraft.eventSuggestions);
              const preAcceptanceStatuses = ['awaiting_intake_confirmation', 'awaiting_tag_review', 'awaiting_event_review'];
              const preActivityStatuses = preAcceptanceStatuses;
              const preGoalStatuses = [...preActivityStatuses, 'awaiting_activity_review'];
              if (Array.isArray(localDraft.selectedEvents) && !preAcceptanceStatuses.includes(localDraft.workflowStatus ?? '')) {
                setSelectedEvents(localDraft.selectedEvents);
              } else if (preAcceptanceStatuses.includes(localDraft.workflowStatus ?? '')) {
                setSelectedEvents([]);
              }
              if (localDraft.activitySuggestions) setActivitySuggestions(localDraft.activitySuggestions);
              if (typeof localDraft.activityReviewCurrentEventIndex === 'number') {
                setActivityReviewCurrentEventIndex(localDraft.activityReviewCurrentEventIndex);
              }
              if (localDraft.selectedActivities && !preActivityStatuses.includes(localDraft.workflowStatus ?? '')) {
                setSelectedActivities(localDraft.selectedActivities);
              } else if (preActivityStatuses.includes(localDraft.workflowStatus ?? '')) {
                setSelectedActivities({});
              }
              if (localDraft.goalSuggestions) setGoalSuggestions(localDraft.goalSuggestions);
              if (localDraft.selectedGoalTemplateIdsByEvent && !preGoalStatuses.includes(localDraft.workflowStatus ?? '')) {
                setSelectedGoalTemplateIdsByEvent(localDraft.selectedGoalTemplateIdsByEvent);
              } else if (preGoalStatuses.includes(localDraft.workflowStatus ?? '')) {
                setSelectedGoalTemplateIdsByEvent({});
              }
              if (Array.isArray(localDraft.serviceSuggestions)) {
                setServiceSuggestions(localDraft.serviceSuggestions);
              }
              if (Array.isArray(localDraft.selectedServiceDefinitionIds)) {
                setSelectedServiceDefinitionIds(localDraft.selectedServiceDefinitionIds);
              }
              if (typeof localDraft.addServicesToShortlist === 'boolean') {
                setAddServicesToShortlist(localDraft.addServicesToShortlist);
              }
              // Pre-set analytics refs for restored local session
              trackedSessionStartRef.current = localDraft.sessionId ?? null;
              trackedStatusRef.current = localDraft.workflowStatus ?? null;
            } else {
              // localStorage draft is for a different session — ignore it
              window.localStorage.removeItem(DRAFT_KEY);
            }
          } catch {
            window.localStorage.removeItem(DRAFT_KEY);
          }
        }
      } finally {
        setIsHydrated(true);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSessionId]);

  useEffect(() => {
    // Don't overwrite localStorage before hydration has finished loading it
    if (!isHydrated) return;

    if (isComplete) {
      window.localStorage.removeItem(DRAFT_KEY);
      return;
    }

    const serializableMessages = messages.map((msg) => ({
      id: msg.id,
      content: msg.content,
      isUser: msg.isUser,
      timestamp: msg.timestamp.toISOString(),
      richData: msg.richData,
    }));

    const draft = {
      messages: serializableMessages,
      sessionId,
      conversationId,
      workflowStatus,
      liveExtractedFields,
      missingFields,
      generatedTags,
      confirmedTags,
      eventSuggestions,
      selectedEvents,
      activitySuggestions,
      activityReviewCurrentEventIndex,
      selectedActivities,
      goalSuggestions,
      selectedGoalTemplateIdsByEvent,
      serviceSuggestions,
      selectedServiceDefinitionIds,
      addServicesToShortlist,
      updatedAt: new Date().toISOString(),
    };

    window.localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  }, [
    isHydrated,
    messages,
    sessionId,
    conversationId,
    workflowStatus,
    liveExtractedFields,
    missingFields,
    generatedTags,
    confirmedTags,
    eventSuggestions,
    selectedEvents,
    activitySuggestions,
    activityReviewCurrentEventIndex,
    selectedActivities,
    goalSuggestions,
    selectedGoalTemplateIdsByEvent,
    serviceSuggestions,
    selectedServiceDefinitionIds,
    addServicesToShortlist,
    isComplete,
  ]);

  // ── Quick-reply chips: patch last bot message after hydration or new turns ──
  // processResponse handles new turns, but session restore rebuilds messages
  // from plain text history with no options attached. This effect covers both
  // paths by watching workflowStatus + missingFields + extractedFields and
  // ensuring the last bot message always carries the right chip options.
  useEffect(() => {
    if (!isHydrated) return;
    if (workflowStatus !== 'awaiting_user_input') return;

    const chips = buildQuickReplies(missingFields, liveExtractedFields);

    setMessages((prev) => {
      // Find the last bot message index
      let lastBotIdx = -1;
      for (let i = prev.length - 1; i >= 0; i -= 1) {
        if (!prev[i].isUser) { lastBotIdx = i; break; }
      }
      if (lastBotIdx === -1) return prev;

      const existing = prev[lastBotIdx];
      // Skip if chips are already identical (avoid infinite render loop)
      const existingIds = (existing.options ?? []).map((o) => o.id).join(',');
      const newIds = (chips ?? []).map((o) => o.id).join(',');
      if (existingIds === newIds) return prev;

      const updated = [...prev];
      updated[lastBotIdx] = { ...existing, options: chips ?? [] };
      return updated;
    });
  }, [isHydrated, workflowStatus, missingFields, liveExtractedFields]);

  // Keep URL in sync with session state
  useEffect(() => {
    if (!isHydrated) return;
    const currentParams = new URLSearchParams(window.location.search);
    const urlSession = currentParams.get('session');
    if (sessionId) {
      // Active session — ensure URL reflects it
      if (urlSession !== sessionId) {
        currentParams.set('session', sessionId);
        const nextSearch = currentParams.toString();
        const nextPath = nextSearch ? `/celebrations/new?${nextSearch}` : '/celebrations/new';
        const currentPath = `${window.location.pathname}${window.location.search}`;
        if (currentPath !== nextPath) {
          navigate(nextPath, { replace: true });
        }
      }
    } else if (urlSession) {
      // URL has a stale/invalid session param — clean it up
      currentParams.delete('session');
      const nextSearch = currentParams.toString();
      const nextPath = nextSearch ? `/celebrations/new?${nextSearch}` : '/celebrations/new';
      const currentPath = `${window.location.pathname}${window.location.search}`;
      if (currentPath !== nextPath) {
        navigate(nextPath, { replace: true });
      }
    }
  }, [sessionId, isHydrated, navigate]);

  useEffect(() => {
    if (!sessionId || !isHydrated) return;

    if (trackedSessionStartRef.current !== sessionId) {
      trackedSessionStartRef.current = sessionId;
      void trackEvent('chat_session_started', {
        sessionId,
        workflowStatus,
      });
    }
  }, [sessionId, workflowStatus, isHydrated, trackEvent]);

  useEffect(() => {
    if (!sessionId || !isHydrated) return;
    if (trackedStatusRef.current === workflowStatus) return;

    trackedStatusRef.current = workflowStatus;
    void trackEvent('chat_step_changed', {
      sessionId,
      workflowStatus,
      step: workflowStatus,
      metadata: {
        missingFieldsCount: missingFields.length,
        hasTags: Object.keys(confirmedTags).length > 0,
        selectedEventsCount: selectedEvents.length,
      },
    });
  }, [sessionId, workflowStatus, missingFields.length, confirmedTags, selectedEvents.length, isHydrated, trackEvent]);

  // Keep interrupt state ref in sync so event listeners read current values
  useEffect(() => {
    interruptStateRef.current = { sessionId, workflowStatus, isComplete };
  }, [sessionId, workflowStatus, isComplete]);

  // Register beforeunload/pagehide listeners once — reads current state from ref
  useEffect(() => {
    const persistPendingInterrupt = (reason: string) => {
      const { sessionId: sid, workflowStatus: ws, isComplete: ic } = interruptStateRef.current;
      if (!sid || ic) return;
      if (ws === 'action_executed' || ws === 'cancelled') return;

      window.localStorage.setItem(PENDING_INTERRUPT_KEY, JSON.stringify({
        sessionId: sid,
        workflowStatus: ws,
        reason,
        at: new Date().toISOString(),
      }));
    };

    const handleBeforeUnload = () => persistPendingInterrupt('page_refresh_or_close');
    const handlePageHide = () => persistPendingInterrupt('page_hidden');

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      persistPendingInterrupt('route_change_or_unmount');
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, []);


  // ── helpers ──────────────────────────────────────────────────

  const addBotMessage = useCallback(
    (content: string, richData?: RichMessageData, options?: MessageOption[]) => {
      const msg: Message = {
        id: `bot-${Date.now()}-${Math.random()}`,
        content,
        isUser: false,
        timestamp: new Date(),
        richData,
        options,
      };
      setMessages((prev) => {
        if (richData?.type !== 'creation_success') {
          return [...prev, msg];
        }

        // Keep only the latest creation success card so stale cards cannot
        // redirect to older celebration ids.
        const withoutPriorSuccess = prev.filter(
          (message) => message.richData?.type !== 'creation_success',
        );
        return [...withoutPriorSuccess, msg];
      });
    },
    [],
  );

  const addUserMessage = useCallback((content: string) => {
    const msg: Message = {
      id: `user-${Date.now()}`,
      content,
      isUser: true,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, msg]);
  }, []);

  const closeAuthPrompt = useCallback(() => {
    isNavigatingToAuthRef.current = false;
    setIsAuthDialogOpen(false);
    setAwaitingAuthForCreate(false);
  }, []);

  const getChatReturnPath = useCallback(() => {
    const params = new URLSearchParams();
    if (sessionId) {
      params.set('session', sessionId);
    }
    if (conversationId) {
      params.set('conversationId', conversationId);
    }
    const query = params.toString();
    return query ? `/celebrations/new?${query}` : '/celebrations/new';
  }, [sessionId, conversationId]);

  const openAuthPopupForCreate = useCallback(async () => {
    const returnPath = getChatReturnPath();
    const returnUrl = new URL(returnPath, window.location.origin).toString();
    const authUrl = buildAuthRedirectToUrl(returnUrl);

    authCompletionHandledRef.current = false;
    setAwaitingAuthForCreate(true);

    if (authPopupRef.current && !authPopupRef.current.closed) {
      authPopupRef.current.focus();
      return;
    }

    try {
      isNavigatingToAuthRef.current = true;
      const doesSessionExist = await Session.doesSessionExist();
      if (doesSessionExist) {
        const payload = await Session.getAccessTokenPayloadSecurely();
        if (payload?.isGuest === true) {
          await Session.signOut();
        }
      }
    } catch {
      // Ignore best-effort guest sign-out failures
    }

    const popup = window.open(
      authUrl,
      'om-auth',
      'popup=yes,width=520,height=760,left=200,top=80',
    );

    if (popup) {
      authPopupRef.current = popup;
      setIsAuthDialogOpen(false);
      popup.focus();
      return;
    }

    isNavigatingToAuthRef.current = false;
    setIsAuthDialogOpen(true);
    addBotMessage(t('celebrations:chatCreation.bot.popupBlocked'));
  }, [getChatReturnPath, addBotMessage]);

  useEffect(() => {
    if (!awaitingAuthForCreate || typeof window === 'undefined') {
      return;
    }

    const watcherId = window.setInterval(async () => {
      const popup = authPopupRef.current;

      if (!popup || popup.closed) {
        if (isAuthenticated && !authCompletionHandledRef.current) {
          authCompletionHandledRef.current = true;
          setIsAuthDialogOpen(false);
          setAwaitingAuthForCreate(false);
          navigate(getChatReturnPath(), { replace: true });
          return;
        }
        isNavigatingToAuthRef.current = false;
        setAwaitingAuthForCreate(false);
        return;
      }

      try {
        const doesSessionExist = await Session.doesSessionExist();
        if (!doesSessionExist) {
          return;
        }

        const payload = await Session.getAccessTokenPayloadSecurely();
        const isGuestSession = payload?.isGuest === true;
        if (isGuestSession) {
          return;
        }

        if (authCompletionHandledRef.current) {
          return;
        }
        authCompletionHandledRef.current = true;

        popup.close();
        authPopupRef.current = null;
        setIsAuthDialogOpen(false);
        setAwaitingAuthForCreate(false);
        navigate(getChatReturnPath(), { replace: true });
      } catch {
        // Ignore transient session/popup checks while auth flow is in progress.
      }
    }, 600);

    return () => {
      window.clearInterval(watcherId);
    };
  }, [awaitingAuthForCreate, isAuthenticated, navigate, getChatReturnPath]);

  useEffect(() => {
    return () => {
      if (authPopupRef.current && !authPopupRef.current.closed) {
        authPopupRef.current.close();
      }
      authPopupRef.current = null;
    };
  }, []);

  /**
   * Derive rich content from the backend response.
   */
  const buildRichData = (data: ChatCreationResponse): RichMessageData | undefined => {
    const status = data.status;

    // Tag review step
    if (status === 'awaiting_tag_review' && data.generatedTags && Object.keys(data.generatedTags).length > 0) {
      return { type: 'tag_review', tags: data.generatedTags };
    }

    if (status === 'awaiting_event_review' && data.eventSuggestions && data.eventSuggestions.length > 0) {
      return {
        type: 'event_review',
        events: data.eventSuggestions,
        activitySuggestions: data.activitySuggestions,
        goalSuggestions: data.goalSuggestions,
      };
    }

    if (
      status === 'awaiting_activity_review' &&
      data.selectedEvents &&
      data.selectedEvents.length > 0 &&
      data.activitySuggestions &&
      Object.keys(data.activitySuggestions).length > 0
    ) {
      return {
        type: 'activity_review',
        selectedEvents: data.selectedEvents,
        activitySuggestions: data.activitySuggestions,
        activityReviewCurrentEventIndex: data.activityReviewCurrentEventIndex,
        selectedActivities: data.selectedActivities,
      };
    }

    if (
      status === 'awaiting_goal_review' &&
      data.selectedEvents &&
      data.selectedEvents.length > 0 &&
      data.goalSuggestions &&
      Object.keys(data.goalSuggestions).length > 0
    ) {
      return {
        type: 'goal_review',
        selectedEvents: data.selectedEvents,
        goalSuggestions: data.goalSuggestions,
      };
    }

    if (
      status === 'awaiting_service_review' &&
      data.serviceSuggestions &&
      data.serviceSuggestions.length > 0
    ) {
      return {
        type: 'service_review',
        services: data.serviceSuggestions,
        selectedServiceDefinitionIds: data.selectedServiceDefinitionIds,
        selectedEvents: data.selectedEvents,
      };
    }

    // Creation success
    if (status === 'action_executed' && data.actionResult?.id) {
      return {
        type: 'creation_success',
        celebrationId: data.actionResult.id,
        celebrationTitle: data.actionResult.title,
        eventsCreated: data.actionResult.eventsCreated,
        activitiesCreated: data.actionResult.activitiesCreated,
        servicesShortlisted: data.actionResult.servicesShortlisted,
        conversationLogId: data.conversationLogId,
      };
    }

    return undefined;
  };

  // ── core send handler ────────────────────────────────────────

  const processResponse = useCallback(
    (data: ChatCreationResponse) => {
      // Persist session / conversation ids (URL sync handled by dedicated effect)
      if (data.sessionId) setSessionId(data.sessionId);
      if (data.conversationId) setConversationId(data.conversationId);

      // Update live extracted fields (merge to keep previous values)
      const normalizedExtractedFields = normalizeCelebrationDraft(data.extractedFields);
      if (Object.keys(normalizedExtractedFields).length > 0) {
        setLiveExtractedFields((prev) => ({ ...prev, ...normalizedExtractedFields }));
      }
      setWorkflowStatus(data.status);
      setIsComplete(data.status === 'action_executed');
      if (Array.isArray(data.missingFields)) {
        setMissingFields(data.missingFields);
      }
      if (data.generatedTags) {
        setGeneratedTags(data.generatedTags);
      }
      if (data.confirmedTags && !['awaiting_tag_review', 'awaiting_intake_confirmation'].includes(data.status)) {
        setConfirmedTags(data.confirmedTags);
      } else if (['awaiting_tag_review', 'awaiting_intake_confirmation'].includes(data.status)) {
        setConfirmedTags({});
      }
      if (data.eventSuggestions) {
        setEventSuggestions(data.eventSuggestions);
      }
      if (typeof data.activityReviewCurrentEventIndex === 'number') {
        setActivityReviewCurrentEventIndex(data.activityReviewCurrentEventIndex);
      } else if (data.status !== 'awaiting_activity_review') {
        setActivityReviewCurrentEventIndex(0);
      }
      // Keep generated selections gated until the user confirms each stage.
      const preAcceptanceStatuses = ['awaiting_intake_confirmation', 'awaiting_tag_review', 'awaiting_event_review'];
      const preActivityStatuses = preAcceptanceStatuses;
      const preGoalStatuses = [...preActivityStatuses, 'awaiting_activity_review'];
      if (data.selectedEvents && !preAcceptanceStatuses.includes(data.status)) {
        setSelectedEvents(data.selectedEvents);
      } else if (preAcceptanceStatuses.includes(data.status)) {
        setSelectedEvents([]);
      }
      if (data.activitySuggestions) {
        setActivitySuggestions(data.activitySuggestions);
      }
      if (typeof data.activityReviewCurrentEventIndex === 'number') {
        setActivityReviewCurrentEventIndex(data.activityReviewCurrentEventIndex);
      } else if (data.status !== 'awaiting_activity_review') {
        setActivityReviewCurrentEventIndex(0);
      }
      if (data.selectedActivities && !preActivityStatuses.includes(data.status)) {
        setSelectedActivities(data.selectedActivities);
      } else if (preActivityStatuses.includes(data.status)) {
        setSelectedActivities({});
      }
      if (data.goalSuggestions) {
        setGoalSuggestions(data.goalSuggestions);
      }
      if (data.selectedGoalTemplateIdsByEvent && !preGoalStatuses.includes(data.status)) {
        setSelectedGoalTemplateIdsByEvent(data.selectedGoalTemplateIdsByEvent);
      } else if (preGoalStatuses.includes(data.status)) {
        setSelectedGoalTemplateIdsByEvent({});
      }
      if (data.serviceSuggestions) {
        setServiceSuggestions(data.serviceSuggestions);
      }
      if (Array.isArray(data.selectedServiceDefinitionIds)) {
        setSelectedServiceDefinitionIds(data.selectedServiceDefinitionIds);
      }
      if (typeof data.addServicesToShortlist === 'boolean') {
        setAddServicesToShortlist(data.addServicesToShortlist);
      }

      if (data.status === 'awaiting_template_selection') {
        const chatTemplateContext: ChatTemplateContext = {
          sessionId: data.sessionId,
          conversationId: data.conversationId,
          extractedFields: data.extractedFields,
          confirmedTags: data.confirmedTags,
          templateSuggestions: data.templateSuggestions,
        };

        const extractedFields = chatTemplateContext.extractedFields;
        const type = typeof extractedFields?.celebration_type === 'string'
          ? extractedFields.celebration_type
          : undefined;
        const subtype = typeof extractedFields?.celebration_subtype === 'string'
          ? extractedFields.celebration_subtype
          : undefined;
        const params = new URLSearchParams({ template: 'true' });
        if (type) params.set('type', type);
        if (subtype) params.set('subtype', subtype);
        navigate(`/quick?${params.toString()}`);
        return;
      }

      const richData = buildRichData(data);
      let buttonOptions: MessageOption[] | undefined;
      if (data.status === 'awaiting_plan_structure_preference') {
        buttonOptions = [
          { id: 'plan-pref-yes', label: t('celebrations:chatCreation.buttons.yes'), value: 'yes' },
          { id: 'plan-pref-no', label: t('celebrations:chatCreation.buttons.no'), value: 'no' },
        ];
      } else if (data.status === 'awaiting_intake_confirmation') {
        buttonOptions = [
          { id: 'intake-confirm-yes', label: t('celebrations:chatCreation.buttons.looksGood'), value: 'yes' },
          { id: 'intake-confirm-edit', label: t('celebrations:chatCreation.buttons.needEdits'), value: 'no' },
        ];
      } else if (data.status === 'awaiting_user_input') {
        // For any field with a known finite option set, attach quick-reply chips.
        buttonOptions = buildQuickReplies(data.missingFields, data.extractedFields);
      }

      // The backend may return multiple response strings
      const text = data.response?.join('\n\n') || '';

      if (text || richData) {
        addBotMessage(text, richData, buttonOptions);
      }

      // Mark complete so we can disable input
      if (data.status === 'action_executed') {
        window.localStorage.removeItem(PENDING_INTERRUPT_KEY);
        window.localStorage.removeItem(DRAFT_KEY);
      }
    },
    [addBotMessage, navigate],
  );

  const sendMessage = useCallback(
    async (content: string, options?: { displayMessage?: string; suppressUserMessage?: boolean }) => {
      if (!content.trim() || isLoading || isComplete) return;

      if (!options?.suppressUserMessage) {
        addUserMessage(options?.displayMessage ?? content);
      }
      void trackEvent('chat_message_sent', {
        step: workflowStatus,
      });
      setIsLoading(true);

      try {
        const data = await sendChatCreationMessage(content, sessionId, conversationId);
        processResponse(data);
      } catch (error: unknown) {
        logError(error instanceof Error ? error : new Error('Chat send failed'));

        let errorText = "Sorry, I couldn't process that. Please try again.";
        const axiosErr = error as { response?: { status?: number; data?: { code?: string; message?: string } } };
        const isLoginRequired = axiosErr?.response?.status === 401 && axiosErr?.response?.data?.code === 'LOGIN_REQUIRED_FOR_CREATE';

        if (isLoginRequired) {
          errorText = 'Please log in or sign up before we can create your celebration. Your chat stays open in the background.';
          void openAuthPopupForCreate();
        } else if (axiosErr?.response?.data?.message) {
          errorText = axiosErr.response.data.message;
        } else if (error instanceof Error) {
          if (error.message.includes('401') || error.message.includes('Unauthorized')) {
            errorText = 'Your session has expired. Please log in again.';
            void openAuthPopupForCreate();
          } else if (error.message.includes('Network Error')) {
            errorText = 'Network error — please check your connection and try again.';
          }
        }
        addBotMessage(errorText);
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, isComplete, sessionId, conversationId, workflowStatus, addUserMessage, addBotMessage, processResponse, trackEvent, openAuthPopupForCreate],
  );

  useEffect(() => {
    if (!isHydrated || isLoading || isComplete) return;
    if (!initialPrompt || hasConsumedInitialPromptRef.current) return;

    hasConsumedInitialPromptRef.current = true;
    sendMessage(initialPrompt);

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('prompt');
    setSearchParams(nextParams, { replace: true });
  }, [isHydrated, isLoading, isComplete, initialPrompt, sendMessage, searchParams, setSearchParams]);

  // ── rich-card callbacks ──────────────────────────────────────

  const handleSelectTemplate = useCallback(
    (_templateKey: string, templateName: string) => {
      sendMessage(templateName);
    },
    [sendMessage],
  );

  const handleSkipTemplate = useCallback(() => {
    sendMessage('skip');
  }, [sendMessage]);

  const handleConfirmTags = useCallback(
    (selectedTags: Record<string, string[]>) => {
      setConfirmedTags(selectedTags);
      void trackEvent('chat_selection_made', {
        step: 'tag_review',
        metadata: {
          selectedTagCount: Object.values(selectedTags).reduce((sum, values) => sum + values.length, 0),
        },
      });
      sendMessage(JSON.stringify(selectedTags), { displayMessage: t('celebrations:chatCreation.confirmations.tags') });
    },
    [sendMessage, trackEvent],
  );

  const handleSkipTags = useCallback(() => {
    void trackEvent('chat_skip_used', { step: 'tag_review' });
    sendMessage('skip tags');
  }, [sendMessage, trackEvent]);

  const handleConfirmEvents = useCallback(
    (selectedEventIds: string[]) => {
      void trackEvent('chat_selection_made', {
        step: 'event_review',
        metadata: {
          selectedEventCount: selectedEventIds.length,
        },
      });
      sendMessage(JSON.stringify({ selectedEventIds }), { displayMessage: t('celebrations:chatCreation.confirmations.events') });
    },
    [sendMessage, trackEvent],
  );

  const handleSkipEvents = useCallback(() => {
    void trackEvent('chat_skip_used', { step: 'event_review' });
    sendMessage('skip events');
  }, [sendMessage, trackEvent]);

  const handleConfirmActivities = useCallback(
    (activities: Record<string, string[]>) => {
      void trackEvent('chat_selection_made', {
        step: 'activity_review',
        metadata: {
          selectedActivityCount: Object.values(activities).reduce((sum, values) => sum + values.length, 0),
        },
      });
      sendMessage(JSON.stringify(activities), { displayMessage: t('celebrations:chatCreation.confirmations.activities') });
    },
    [sendMessage, trackEvent],
  );

  const handleSkipActivities = useCallback(() => {
    void trackEvent('chat_skip_used', { step: 'activity_review' });
    sendMessage('skip activities');
  }, [sendMessage, trackEvent]);

  const handlePreviousActivities = useCallback(() => {
    sendMessage('previous activities', { displayMessage: t('celebrations:chatCreation.confirmations.activities') });
  }, [sendMessage, t]);

  const handleConfirmGoals = useCallback(
    (goalTemplateIdsByEvent: Record<string, number[]>) => {
      void trackEvent('chat_selection_made', {
        step: 'goal_review',
        metadata: {
          selectedGoalCount: Object.values(goalTemplateIdsByEvent).reduce((sum, values) => sum + values.length, 0),
        },
      });
      sendMessage(
        JSON.stringify({ selectedGoalTemplateIdsByEvent: goalTemplateIdsByEvent }),
        { displayMessage: t('celebrations:chatCreation.confirmations.goals') },
      );
    },
    [sendMessage, trackEvent],
  );

  const handleSkipGoals = useCallback(() => {
    void trackEvent('chat_skip_used', { step: 'goal_review' });
    sendMessage('skip goals');
  }, [sendMessage, trackEvent]);

  const handleConfirmServices = useCallback(
    (serviceDefinitionIds: number[]) => {
      setSelectedServiceDefinitionIds(serviceDefinitionIds);
      setAddServicesToShortlist(serviceDefinitionIds.length > 0);
      void trackEvent('chat_selection_made', {
        step: 'service_review',
        metadata: {
          selectedServiceCount: serviceDefinitionIds.length,
        },
      });
      sendMessage(
        JSON.stringify({ selectedServiceDefinitionIds: serviceDefinitionIds }),
        { displayMessage: t('celebrations:chatCreation.confirmations.services') },
      );
    },
    [sendMessage, trackEvent],
  );

  const handleSkipServices = useCallback(() => {
    setSelectedServiceDefinitionIds([]);
    setAddServicesToShortlist(false);
    void trackEvent('chat_skip_used', { step: 'service_review' });
    sendMessage('skip services');
  }, [sendMessage, trackEvent]);

  const handleNavigateCelebration = useCallback(
    (celebrationId: number) => {
      navigate(`/celebrations/${celebrationId}`);
    },
    [navigate],
  );

  const switchToQuickCreate = useCallback(() => {
    writeCrossModeDraft({
      title: typeof liveExtractedFields.title === 'string' ? liveExtractedFields.title : undefined,
      description: typeof liveExtractedFields.description === 'string' ? liveExtractedFields.description : undefined,
      celebration_type: typeof liveExtractedFields.celebration_type === 'string' ? liveExtractedFields.celebration_type : undefined,
      celebration_subtype: typeof liveExtractedFields.celebration_subtype === 'string' ? liveExtractedFields.celebration_subtype : undefined,
      template: typeof liveExtractedFields.template === 'string' ? liveExtractedFields.template : undefined,
      start_date: typeof liveExtractedFields.start_date === 'string' ? liveExtractedFields.start_date : undefined,
      end_date: typeof liveExtractedFields.end_date === 'string' ? liveExtractedFields.end_date : undefined,
      budget: typeof liveExtractedFields.budget === 'number' ? liveExtractedFields.budget : undefined,
      location_name: typeof liveExtractedFields.location_name === 'string' ? liveExtractedFields.location_name : undefined,
      latitude: typeof liveExtractedFields.latitude === 'number' ? liveExtractedFields.latitude : undefined,
      longitude: typeof liveExtractedFields.longitude === 'number' ? liveExtractedFields.longitude : undefined,
      radius_miles: typeof liveExtractedFields.radius_miles === 'number' ? liveExtractedFields.radius_miles : undefined,
      rsvp_deadline: typeof liveExtractedFields.rsvp_deadline === 'string' ? liveExtractedFields.rsvp_deadline : undefined,
      celebrant_names: toStringArray(liveExtractedFields.celebrant_names),
      celebrant_roles: toStringArray(liveExtractedFields.celebrant_roles),
      celebrant_emails: toStringArray(liveExtractedFields.celebrant_emails),
      host_name: toStringArray(liveExtractedFields.celebrant_names)[0],
    });

    const draftQuery = draftId ? `?draft=${encodeURIComponent(draftId)}` : '';
    navigate(`/quick${draftQuery}`);
  }, [draftId, liveExtractedFields, navigate]);

  useEffect(() => {
    if (Object.keys(liveExtractedFields).length > 0) {
      return;
    }

    const crossModeDraft = readCrossModeDraft();
    if (!crossModeDraft) {
      return;
    }

    const seedMessages: string[] = [];
    if (crossModeDraft.title) seedMessages.push(`Title: ${crossModeDraft.title}`);
    if (crossModeDraft.celebration_type) seedMessages.push(`Type: ${crossModeDraft.celebration_type}`);
    if (crossModeDraft.start_date) seedMessages.push(`Start date: ${crossModeDraft.start_date}`);
    if (crossModeDraft.end_date) seedMessages.push(`End date: ${crossModeDraft.end_date}`);
    if (crossModeDraft.location_name) seedMessages.push(`Location: ${crossModeDraft.location_name}`);
    if (crossModeDraft.celebrant_names && crossModeDraft.celebrant_names.length > 0) {
      seedMessages.push(`Celebrants: ${crossModeDraft.celebrant_names.join(', ')}`);
    }

    if (seedMessages.length === 0) {
      return;
    }

    const hydratedDraft: CelebrationDraftDto = {
      ...(crossModeDraft.title ? { title: crossModeDraft.title } : {}),
      ...(crossModeDraft.description ? { description: crossModeDraft.description } : {}),
      ...(crossModeDraft.celebration_type ? { celebration_type: crossModeDraft.celebration_type } : {}),
      ...(crossModeDraft.celebration_subtype ? { celebration_subtype: crossModeDraft.celebration_subtype } : {}),
      ...(crossModeDraft.template ? { template: crossModeDraft.template } : {}),
      ...(crossModeDraft.start_date ? { start_date: crossModeDraft.start_date } : {}),
      ...(crossModeDraft.end_date ? { end_date: crossModeDraft.end_date } : {}),
      ...(!crossModeDraft.start_date && crossModeDraft.event_date ? { start_date: crossModeDraft.event_date } : {}),
      ...(!crossModeDraft.end_date && crossModeDraft.event_date ? { end_date: crossModeDraft.event_date } : {}),
      ...(typeof crossModeDraft.budget === 'number' ? { budget: crossModeDraft.budget } : {}),
      ...(crossModeDraft.location_name ? { location_name: crossModeDraft.location_name } : {}),
      ...(typeof crossModeDraft.latitude === 'number' ? { latitude: crossModeDraft.latitude } : {}),
      ...(typeof crossModeDraft.longitude === 'number' ? { longitude: crossModeDraft.longitude } : {}),
      ...(typeof crossModeDraft.radius_miles === 'number' ? { radius_miles: crossModeDraft.radius_miles } : {}),
      ...(crossModeDraft.rsvp_deadline ? { rsvp_deadline: crossModeDraft.rsvp_deadline } : {}),
      ...(crossModeDraft.celebrant_names && crossModeDraft.celebrant_names.length > 0
        ? { celebrant_names: crossModeDraft.celebrant_names }
        : crossModeDraft.host_name
          ? { celebrant_names: [crossModeDraft.host_name] }
          : {}),
      ...(crossModeDraft.celebrant_roles && crossModeDraft.celebrant_roles.length > 0 ? { celebrant_roles: crossModeDraft.celebrant_roles } : {}),
      ...(crossModeDraft.celebrant_emails && crossModeDraft.celebrant_emails.length > 0 ? { celebrant_emails: crossModeDraft.celebrant_emails } : {}),
    };

    if (Object.keys(hydratedDraft).length > 0) {
      setLiveExtractedFields(hydratedDraft);
    }

    addBotMessage(`I found your saved draft and will continue from it:\n${seedMessages.map((line) => `- ${line}`).join('\n')}`);
  }, [liveExtractedFields, addBotMessage]);

  const handleCreateCelebration = useCallback(async () => {
    if (isLoading || isComplete) {
      return;
    }

    // Pre-creation validation: if any celebrant is missing an email, route through
    // the chat so the ValidatorNode can ask for them before creation proceeds.
    const celebrantNames = toStringArray(liveExtractedFields.celebrant_names);
    const celebrantEmails = toStringArray(liveExtractedFields.celebrant_emails);
    if (celebrantNames.length > 0 && celebrantNames.some((_, i) => !celebrantEmails[i])) {
      sendMessage('create celebration', { suppressUserMessage: true });
      return;
    }

    const createInput = buildCreateWorkflowInput({
      draft: liveExtractedFields,
      chatSessionId: sessionId,
      confirmedTags,
      selectedEvents,
      selectedActivities,
      selectedGoalTemplateIdsByEvent,
      addServicesToShortlist,
      selectedServiceDefinitionIds,
    });

    if (!createInput) {
      addBotMessage(t('celebrations:chatCreation.bot.missingDetails'));
      return;
    }

    void trackEvent('chat_create_clicked', {
      step: workflowStatus,
      metadata: {
        selectedEventsCount: selectedEvents.length,
        selectedActivitiesCount: Object.values(selectedActivities).reduce((sum, values) => sum + values.length, 0),
        selectedServicesCount: selectedServiceDefinitionIds.length,
      },
    });

    setIsLoading(true);

    try {
      const result = await createCelebrationWithWorkflow(createInput);
      if (draftId) {
        void celebrationService.completeDraft(draftId).catch(() => undefined);
      }
      const warnings = getWorkflowWarnings(result);
      const mappingErrors = getWorkflowMappingErrors(result);
      const warningMessageSet = new Set(warnings.map((warning) => warning.trim().toLowerCase()));
      const uniqueMappingErrors = mappingErrors.filter(
        (mappingError) => !warningMessageSet.has(mappingError.message.trim().toLowerCase()),
      );
      const hasPartialFailures = warnings.length > 0 || uniqueMappingErrors.length > 0;

      setWorkflowStatus('action_executed');
      setIsComplete(true);
      window.localStorage.removeItem(PENDING_INTERRUPT_KEY);
      window.localStorage.removeItem(DRAFT_KEY);
      clearCrossModeDraft();

      const selectedActivityCount = Object.values(selectedActivities).reduce((sum, values) => sum + values.length, 0);
      const eventsCreatedCount = result.metrics.eventsCreated > 0
        ? result.metrics.eventsCreated
        : Object.keys(result.createdEventIdsBySource).length;
      const activitiesCreatedCount = result.metrics.activitiesCreated > 0
        ? result.metrics.activitiesCreated
        : selectedActivityCount;

      addBotMessage(
        hasPartialFailures
          ? t('celebrations:chatCreation.bot.celebrationReadyWithWarnings')
          : t('celebrations:chatCreation.bot.celebrationReady'),
        {
          type: 'creation_success',
          celebrationId: result.celebrationId,
          celebrationTitle: createInput.celebrationData.title,
          eventsCreated: eventsCreatedCount,
          activitiesCreated: activitiesCreatedCount,
          servicesShortlisted: result.metrics.servicesShortlisted,
        },
      );

      if (warnings.length > 0) {
        addBotMessage(formatWarningsMessage(warnings));
      }

      if (uniqueMappingErrors.length > 0) {
        addBotMessage(formatMappingErrorsMessage(uniqueMappingErrors));
      }

      if (hasPartialFailures && result.logId) {
        addBotMessage(`LogId: ${result.logId}`);
      }
    } catch (error) {
      logError(error instanceof Error ? error : new Error('Chat create workflow failed'));
      addBotMessage(handleApiError(error) || t('celebrations:chatCreation.bot.creationFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [
    isLoading,
    isComplete,
    liveExtractedFields,
    sessionId,
    confirmedTags,
    selectedEvents,
    selectedActivities,
    selectedGoalTemplateIdsByEvent,
    addServicesToShortlist,
    selectedServiceDefinitionIds,
    sendMessage,
    addBotMessage,
    trackEvent,
    workflowStatus,
    PENDING_INTERRUPT_KEY,
    DRAFT_KEY,
  ]);

  const handleSelectOption = useCallback(
    (_optionId: string | number, optionValue?: string) => {
      if (optionValue === '__open_calendar__' || _optionId === 'quick-calendar-picker') {
        setIsCalendarModalOpen(true);
        return;
      }

      if (optionValue === CREATE_CELEBRATION_OPTION_VALUE) {
        if (!isAuthenticated) {
          addBotMessage(
            t('celebrations:chatCreation.bot.loginRequired'),
            undefined,
            [{ id: 'login-signup', label: t('celebrations:chatCreation.buttons.loginSignup'), value: LOGIN_SIGNUP_OPTION_VALUE, variant: 'auth' }],
          );
          return;
        }

        void handleCreateCelebration();
        return;
      }

      if (optionValue === LOGIN_SIGNUP_OPTION_VALUE) {
        const returnPath = getChatReturnPath();
        if (typeof window !== 'undefined') {
          const returnUrl = new URL(returnPath, window.location.origin).toString();
          window.location.assign(buildAuthRedirectToUrl(returnUrl));
          return;
        }

        navigate(`/auth?redirectToPath=${encodeURIComponent(returnPath)}`);
        return;
      }

      // For chip quick-replies: send the machine value but show the human label in the bubble.
      // The label is stored on the option; find it from the current messages.
      const text = optionValue || String(_optionId);
      // Find the label for this option id so we can display it instead of the raw value.
      const matchedOption = messages
        .flatMap((m) => m.options ?? [])
        .find((o) => o.id === _optionId && o.variant === 'chip');
      const displayMessage = matchedOption ? matchedOption.label : undefined;
      sendMessage(text, displayMessage ? { displayMessage } : undefined);
    },
    [sendMessage, isAuthenticated, addBotMessage, navigate, getChatReturnPath, handleCreateCelebration, messages],
  );

  const handleConfirmCalendarDate = useCallback(
    (dateStr: string, isVenueTbd?: boolean) => {
      setIsCalendarModalOpen(false);
      const text = isVenueTbd ? `${dateStr}, venue is TBD` : dateStr;
      const displayMessage = isVenueTbd ? `📅 ${dateStr} (Venue TBD)` : `📅 ${dateStr}`;
      sendMessage(text, { displayMessage });
    },
    [sendMessage],
  );

  useEffect(() => {
    if (!isHydrated || isComplete || workflowStatus !== 'awaiting_create_action' || !isCreateActionValidationPassed) {
      createActionCtaStateRef.current = null;
      setMessages((prev) => prev.filter((message) => !isCreateActionCtaMessage(message)));
      return;
    }

    const ctaState: 'guest' | 'authenticated' = isAuthenticated ? 'authenticated' : 'guest';
    if (createActionCtaStateRef.current === ctaState) {
      return;
    }
    createActionCtaStateRef.current = ctaState;

    setMessages((prev) => {
      const withoutCta = prev.filter((message) => !isCreateActionCtaMessage(message));

      const ctaMessage: Message = isAuthenticated
        ? {
            id: `bot-create-cta-${Date.now()}`,
            content: 'Everything looks good on my side. When you are ready, click Create Celebration and I will finalize it.',
            isUser: false,
            timestamp: new Date(),
            options: [{ id: 'create-celebration-inline', label: '🎉 Create Celebration', value: CREATE_CELEBRATION_OPTION_VALUE, variant: 'primary' }],
          }
        : {
            id: `bot-login-cta-${Date.now()}`,
            content: 'To create your celebration, please log in or sign up first.',
            isUser: false,
            timestamp: new Date(),
            options: [{ id: 'login-signup-inline', label: 'Login / Signup', value: LOGIN_SIGNUP_OPTION_VALUE, variant: 'auth' }],
          };

      return [...withoutCta, ctaMessage];
    });
  }, [isHydrated, isComplete, workflowStatus, isAuthenticated, isCreateActionCtaMessage, isCreateActionValidationPassed]);

  // ── guard: auth store still syncing ──────────────────────────
  if (isAuthLoading) {
    return (
      <div className="container mx-auto h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          <p className="mt-2 text-sm text-muted-foreground">{t('celebrations:chatCreation.loading')}</p>
        </div>
      </div>
    );
  }

  // ── render ───────────────────────────────────────────────────

  const previewPanelProps = {
    extractedFields: liveExtractedFields,
    confirmedTags,
    selectedEvents,
    selectedActivities,
    activityReviewCurrentEventIndex,
    goalSuggestions,
    selectedGoalTemplateIdsByEvent,
    serviceSuggestions,
    selectedServiceDefinitionIds,
    missingFields,
    workflowStatus,
    collapsed: Object.keys(liveExtractedFields).length === 0 && Object.keys(confirmedTags).length === 0 && selectedEvents.length === 0,
  };

  return (
    <div className="h-full w-full overflow-hidden">
      <Dialog open={isAuthDialogOpen} onClose={closeAuthPrompt}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('celebrations:chatCreation.auth.dialogTitle')}</DialogTitle>
            <DialogDescription>
              {t('celebrations:chatCreation.auth.dialogDescription')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={closeAuthPrompt}>{t('celebrations:chatCreation.auth.cancel')}</Button>
            <Button onClick={() => { void openAuthPopupForCreate(); }}>{t('celebrations:chatCreation.auth.loginSignup')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CalendarPickerModal
        open={isCalendarModalOpen}
        onClose={() => setIsCalendarModalOpen(false)}
        onSelectDate={handleConfirmCalendarDate}
        showVenueTbdOption={missingFields.includes('location_name')}
        initialDate={typeof liveExtractedFields.start_date === 'string' ? liveExtractedFields.start_date : undefined}
      />

      <SplitCreationLayout
        defaultChatWidthPct={45}
        chatPanel={
          <Card className="flex flex-col h-full min-h-0">
            {/* Header */}
            <div className="p-4 border-b flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">{t('celebrations:chatCreation.header.title')}</h2>
                <p className="text-sm text-muted-foreground">
                  {t('celebrations:chatCreation.header.subtitle')}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={switchToQuickCreate}
                  className="text-sm text-muted-foreground hover:text-foreground underline"
                >
                  Continue in Quick Create
                </button>
              </div>
            </div>

            {/* Messages */}
            <MessageList
              messages={messages}
              isLoading={isLoading}
              sessionId={sessionId}
              workflowStatus={workflowStatus}
              onSelectOption={handleSelectOption}
              onOpenCalendar={() => setIsCalendarModalOpen(true)}
              onSelectTemplate={handleSelectTemplate}
              onSkipTemplate={handleSkipTemplate}
              onNavigateCelebration={handleNavigateCelebration}
              onConfirmTags={handleConfirmTags}
              onSkipTags={handleSkipTags}
              onConfirmEvents={handleConfirmEvents}
              onSkipEvents={handleSkipEvents}
              onConfirmActivities={handleConfirmActivities}
              onPreviousActivities={handlePreviousActivities}
              onSkipActivities={handleSkipActivities}
              onConfirmGoals={handleConfirmGoals}
              onSkipGoals={handleSkipGoals}
              onConfirmServices={handleConfirmServices}
              onSkipServices={handleSkipServices}
            />

            {/* Input */}
            <InputControls
              onSendMessage={sendMessage}
              isLoading={isLoading || isComplete}
              onOpenCalendar={() => setIsCalendarModalOpen(true)}
            />
          </Card>
        }
        previewPanel={<CreationPreviewPanel {...previewPanelProps} />}
      />
    </div>
  );
};

